/*
 * Helmet size finder: <lux-helmet-finder>, rendered by
 * snippets/lux-helmet-finder.liquid.
 *
 * Reads the collection feed (templates/collection.helmetfinder.liquid), works
 * out each variant's head circumference range from its product's size chart,
 * and lists every helmet with an available variant that fits the number the
 * shopper typed. Helmets with a fitting variant on the shelf at LUXBMX come
 * first, the rest after, each group in the collection's own order.
 *
 * How a variant's Size value is matched to a chart row, in order:
 *   1. the value carries its own range ("Small/Medium 51-56 cm"): use it
 *   2. the value names a row once both are normalised, so "XS/SM", "XS/S"
 *      and "XS-S" are the same size, and "Medium" or "MD" is "M"
 *   3. the same, ignoring a Youth / Kids / Junior prefix
 *   4. one size (or no size option): the chart's only row, or its full span
 * A variant that matches none of these is left out rather than guessed at.
 *
 * Each card can be switched between the fitting colours and sizes and added
 * to the cart from the results. The last measurement is kept in localStorage
 * so helmet product pages can show "Your size" (<lux-helmet-fit>, below).
 */
(function () {
  if (customElements.get('lux-helmet-finder')) return;

  var CM_PER_IN = 2.54;
  var MIN_CM = 40;
  var MAX_CM = 70;
  var STORE_KEY = 'lux-head-cm';

  /* Chip colour for a colour group whose colour metaobject has no hex set. */
  var GROUP_HEX = {
    'colour-black': '#000000', 'colour-white': '#ffffff', 'colour-grey': '#8a8d91',
    'colour-blue': '#2f5fb3', 'colour-green': '#4c8a3f', 'colour-red': '#c8262c',
    'colour-orange': '#f07c1b', 'colour-yellow': '#f5d02a', 'colour-pink': '#ef6fae',
    'colour-purple': '#7342a8', 'colour-teal': '#14a3a8', 'colour-brown': '#7a5230',
    'colour-beige': '#d9c7a3', 'colour-gold': '#c9a43b', 'colour-chrome': '#c7cbd1'
  };

  /* Chip dot for a colour group: its swatch image, then its hex, the same
     order as the theme's own swatches (snippets/lux-color-swatch-style.liquid). */
  function dotStyle(info, handle) {
    if (info && info.image) return ' style="background:url(' + esc(info.image) + ') center / cover"';
    var hex = (info && info.hex) || GROUP_HEX[handle];
    return hex ? ' style="background:' + esc(hex) + '"' : '';
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fmt(n) {
    return String(Math.round(n * 10) / 10);
  }

  function parseRange(text) {
    var m = String(text || '').match(/(\d+(?:\.\d+)?)\s*(?:-|\u2013|\u2014|to)\s*(\d+(?:\.\d+)?)/i);
    if (!m) return null;
    var a = parseFloat(m[1]);
    var b = parseFloat(m[2]);
    return { min: Math.min(a, b), max: Math.max(a, b) };
  }

  function norm(label) {
    return String(label || '')
      .toUpperCase()
      .replace(/\bXX[\s-]?LARGE\b/g, 'XXL')
      .replace(/\bX[\s-]?LARGE\b/g, 'XL')
      .replace(/\bXX[\s-]?SMALL\b/g, 'XXS')
      .replace(/\bX[\s-]?SMALL\b/g, 'XS')
      .replace(/\bSMALL\b|\bSM\b/g, 'S')
      .replace(/\bMEDIUM\b|\bMED\b|\bMD\b/g, 'M')
      .replace(/\bLARGE\b|\bLG\b/g, 'L')
      .replace(/\//g, '-')
      .replace(/\s+/g, '');
  }

  function stripAge(n) {
    return n.replace(/^(YOUTH|YTH|KIDS|KID|JUNIOR|JR)/, '');
  }

  /* The last measurement used, so helmet product pages can show "Your size". */
  function saveHead(cm) {
    try { localStorage.setItem(STORE_KEY, String(Math.round(cm * 10) / 10)); } catch (e) { /* storage unavailable */ }
  }
  function savedHead() {
    try {
      var cm = parseFloat(localStorage.getItem(STORE_KEY));
      return cm >= MIN_CM && cm <= MAX_CM ? cm : null;
    } catch (e) { return null; }
  }

  function guideRows(guide) {
    var cols = guide.columns || [];
    var headIdx = cols.findIndex(function (c) { return /circumference|head/i.test(c); });
    if (headIdx < 0) headIdx = 1;
    var rows = [];
    (guide.rows || []).forEach(function (row) {
      var cells = String(row).split('|').map(function (c) { return c.trim(); });
      var range = parseRange(cells[headIdx]);
      if (!cells[0] || !range) return;
      rows.push({ label: cells[0], norm: norm(cells[0]), min: range.min, max: range.max });
    });
    return rows;
  }

  function rangeFor(value, rows) {
    if (!rows.length) return null;
    var own = /cm/i.test(value || '') ? parseRange(value) : null;
    if (own) return own;
    var n = norm(value);
    var hit = rows.find(function (r) { return r.norm === n; }) ||
      rows.find(function (r) { return stripAge(r.norm) === stripAge(n); });
    if (hit) return { min: hit.min, max: hit.max };
    if (!value || /^ONESIZE|^OS$/.test(n)) {
      return {
        min: Math.min.apply(null, rows.map(function (r) { return r.min; })),
        max: Math.max.apply(null, rows.map(function (r) { return r.max; }))
      };
    }
    return null;
  }

  /* Turns a feed product into { product, variants: [{ variant, size, colour, range }] } */
  function prepare(product) {
    var rows = guideRows(product.guide || {});
    var sizeIdx = (product.options || []).findIndex(function (o) { return /size/i.test(o); });
    var colourIdx = (product.options || []).findIndex(function (o) { return /colou?r/i.test(o); });
    var variants = [];
    (product.variants || []).forEach(function (v) {
      var size = sizeIdx >= 0 ? v.options[sizeIdx] : '';
      var range = rangeFor(size, rows);
      if (!range) return;
      var colour = colourIdx >= 0 ? v.options[colourIdx] : '';
      if (colour === '-') colour = '';
      variants.push({ v: v, size: size, colour: colour, range: range });
    });
    return { p: product, variants: variants };
  }

  /* Helmet styles (variant global.type), in chip order, with their chip names. */
  var STYLES = [['Full Face', 'Race full face'], ['Open Face', 'Skate style'], ['Full Cut', 'Full cut']];
  var STYLE_NAME = {};
  STYLES.forEach(function (s) { STYLE_NAME[s[0]] = s[1]; });
  function styleOf(x) { return STYLE_NAME[(x.v.style || '').trim()] ? x.v.style.trim() : ''; }

  class LuxHelmetFinder extends HTMLElement {
    connectedCallback() {
      this.unit = 'cm';
      this.colour = '';
      this.helmetStyle = '';
      this.items = null;
      this.colourInfo = {};
      this.input = this.querySelector('[data-head]');
      this.status = this.querySelector('[data-status]');
      this.results = this.querySelector('[data-results]');
      this.chipsWrap = this.querySelector('[data-colours]');
      this.chips = this.querySelector('[data-chips]');
      this.styleWrap = this.querySelector('[data-styles]');
      this.styleChips = this.querySelector('[data-style-chips]');
      // The collection grid, filters and paging, hidden while results show.
      this.grid = document.querySelector('product-facet');

      var self = this;
      var timer;
      this.querySelector('form').addEventListener('submit', function (e) {
        e.preventDefault();
        self.update();
      });
      this.input.addEventListener('focus', function () { self.load(); }, { once: true });
      this.input.addEventListener('input', function () {
        if (self.input.value) self.setOpen(true, false);
        self.pinnedCm = null;
        clearTimeout(timer);
        timer = setTimeout(function () { self.update(); }, 250);
      });
      this.querySelectorAll('[data-unit]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.setUnit(btn.dataset.unit, true); });
      });
      this.chips.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-colour]');
        if (!chip) return;
        self.colour = chip.dataset.colour === self.colour ? '' : chip.dataset.colour;
        self.update();
      });
      this.styleChips.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-style]');
        if (!chip) return;
        self.helmetStyle = chip.dataset.style === self.helmetStyle ? '' : chip.dataset.style;
        self.update();
      });
      this.querySelector('[data-toggle]').addEventListener('click', function () {
        self.setOpen(this.getAttribute('aria-expanded') !== 'true', true);
      });
      this.results.addEventListener('click', function (e) { self.onResultsClick(e); });
      this.status.addEventListener('click', function (e) {
        if (e.target.closest('[data-clear]')) self.clear();
      });

      var params = new URLSearchParams(location.search);
      if (params.get('unit') === 'in') this.setUnit('in', false);
      if (params.get('colour')) this.colour = params.get('colour');
      if (STYLE_NAME[params.get('style')]) this.helmetStyle = params.get('style');
      if (params.get('head')) this.setOpen(true, false);
      if (params.get('head')) {
        this.input.value = params.get('head');
        this.update();
      }
    }

    /* Closed, the panel is the heading and the measurement field: the
       filters open from the heading, or by themselves as soon as someone
       types a measurement or the link already carries one. */
    setOpen(open, focus) {
      var panel = this.querySelector('[data-panel]');
      var btn = this.querySelector('[data-toggle]');
      panel.classList.toggle('is-collapsed', !open);
      btn.setAttribute('aria-expanded', String(open));
      this.querySelector('[data-filters]').hidden = !open;
      if (open && focus) {
        // The cm box, or the feet box when ft/in is on.
        var field = Array.prototype.find.call(this.querySelectorAll('form input'), function (el) { return el.offsetParent !== null; });
        if (field) field.focus();
      }
    }

    setUnit(unit, convert) {
      if (unit === this.unit) return;
      var value = parseFloat(this.input.value);
      // Switching units keeps the exact measurement, so the rounded figure in
      // the box does not shift a boundary size in or out of the results.
      this.pinnedCm = convert && value > 0 ? this.headCm() : null;
      if (convert && value > 0) {
        this.input.value = unit === 'in' ? fmt(value / CM_PER_IN) : fmt(Math.round(value * CM_PER_IN * 2) / 2);
      }
      this.unit = unit;
      this.input.placeholder = unit === 'in' ? 'e.g. 22' : 'e.g. 56';
      this.input.step = unit === 'in' ? '0.1' : '0.5';
      this.querySelectorAll('[data-unit]').forEach(function (btn) {
        var on = btn.dataset.unit === unit;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      if (convert) this.update();
    }

    load() {
      if (this.loading) return this.loading;
      var self = this;
      var feed = this.dataset.feed;
      function page(n) {
        return fetch(feed + '&page=' + n, { credentials: 'same-origin' }).then(function (r) {
          if (!r.ok) throw new Error('Feed ' + r.status);
          return r.json();
        });
      }
      this.loading = page(1).then(function (first) {
        var more = [];
        for (var n = 2; n <= (first.pages || 1); n++) more.push(page(n));
        return Promise.all(more).then(function (rest) {
          var pages = [first].concat(rest);
          self.items = [];
          pages.forEach(function (pg) {
            (pg.products || []).forEach(function (p) {
              var item = prepare(p);
              if (item.variants.length) self.items.push(item);
            });
            Object.assign(self.colourInfo, pg.colours || {});
          });
        });
      }).catch(function (err) {
        self.loading = null;
        throw err;
      });
      return this.loading;
    }

    headCm() {
      var value = parseFloat(this.input.value);
      if (!(value > 0)) return null;
      if (this.pinnedCm) return this.pinnedCm;
      return this.unit === 'in' ? value * CM_PER_IN : value;
    }

    update() {
      var cm = this.headCm();
      this.syncUrl();
      if (cm == null) {
        this.showGrid(true);
        this.status.innerHTML = '';
        this.results.hidden = true;
        this.chipsWrap.hidden = true;
        return;
      }
      if (cm < MIN_CM || cm > MAX_CM) {
        this.showGrid(true);
        this.results.hidden = true;
        this.chipsWrap.hidden = true;
        var hint = this.unit === 'cm' && cm < 30 ? ' If you measured in inches, switch to in.' : '';
        this.status.textContent = 'Head measurements are usually between 45 and 65cm.' + hint;
        return;
      }
      var self = this;
      if (!this.items) {
        this.status.textContent = 'Finding helmets in your size\u2026';
        this.load().then(function () { self.update(); }, function () {
          self.status.textContent = 'The size finder could not load. Refresh the page to try again.';
        });
        return;
      }
      this.render(cm);
    }

    render(cm) {
      var colour = this.colour;
      var style = this.helmetStyle;
      var luxList = [];
      var otherList = [];
      var colourCounts = {};
      var styleCounts = {};
      var cards = this.cards = [];
      saveHead(cm);

      this.items.forEach(function (item) {
        var fitting = item.variants.filter(function (x) { return cm >= x.range.min && cm <= x.range.max; });
        if (!fitting.length) return;

        // Each row of chips counts helmets under the other row's choice, so a
        // count is always what picking that chip would show.
        var inStyle = style ? fitting.filter(function (x) { return styleOf(x) === style; }) : fitting;
        var inColour = colour ? fitting.filter(function (x) { return (x.v.colours || []).indexOf(colour) !== -1; }) : fitting;
        var seen = {};
        inStyle.forEach(function (x) {
          (x.v.colours || []).forEach(function (c) {
            if (!seen[c]) { seen[c] = true; colourCounts[c] = (colourCounts[c] || 0) + 1; }
          });
        });
        var seenStyle = {};
        inColour.forEach(function (x) {
          var st = styleOf(x);
          if (st && !seenStyle[st]) { seenStyle[st] = true; styleCounts[st] = (styleCounts[st] || 0) + 1; }
        });

        fitting = inStyle.filter(function (x) { return inColour.indexOf(x) !== -1; });
        if (!fitting.length) return;

        var best = fitting.reduce(function (a, b) { return b.v.lux > a.v.lux ? b : a; }, fitting[0]);
        var card = { item: item, fitting: fitting, best: best, index: cards.length };
        cards.push(card);
        (best.v.lux > 0 ? luxList : otherList).push(card);
      });

      this.renderChips(colourCounts);
      this.renderStyleChips(styleCounts);

      var total = luxList.length + otherList.length;
      var size = this.unit === 'in'
        ? fmt(parseFloat(this.input.value)) + 'in (' + fmt(cm) + 'cm)'
        : fmt(cm) + 'cm';
      var colourName = colour && this.colourInfo[colour] ? ' in ' + this.colourInfo[colour].name.toLowerCase() : '';
      var styleName = style ? STYLE_NAME[style].toLowerCase() + ' ' : '';
      var line;
      if (!total) {
        line = 'No ' + styleName + 'helmets' + colourName + ' come in a size for a ' + size + ' head right now.';
      } else {
        line = total + ' ' + styleName + (total === 1 ? 'helmet' : 'helmets') + colourName + ' fit a ' + size + ' head';
        line += luxList.length ? ', ' + luxList.length + ' in stock at LUXBMX.' : '.';
      }
      this.status.innerHTML = esc(line) + '<button type="button" class="lux-hf__clear link" data-clear>Clear</button>';

      var html = '';
      if (luxList.length) html += this.group(this.dataset.luxLabel, luxList);
      if (otherList.length) html += this.group(this.dataset.otherLabel, otherList);
      if (!total && (colour || style)) {
        html = '<p class="lux-hf__empty">Try another ' + (colour && style ? 'colour or style' : colour ? 'colour' : 'style') +
          ', or <button type="button" class="link" data-any>show every helmet</button>.</p>';
      }
      this.results.innerHTML = html;
      var anyBtn = this.results.querySelector('[data-any]');
      var self = this;
      if (anyBtn) anyBtn.addEventListener('click', function () { self.colour = ''; self.helmetStyle = ''; self.update(); });
      this.results.hidden = !html;
      this.showGrid(false);
      this.loadRatings();
    }

    /* Reviews.io only scans for star placeholders when its app embed starts,
       so cards added later stay empty. Run its snippet again with the app
       embed's settings; placeholders it has already filled are left alone. */
    loadRatings() {
      if (typeof window.ratingSnippet !== 'function') return;
      var store = window.__REVIEWSIO_RATING_SNIPPET_STORE_OVERRIDE__ || window.reviewsIoStore;
      if (!store) return;
      window.ratingSnippet('ruk_rating_snippet', {
        store: store,
        color: '#000000',
        textClr: '#333333',
        linebreak: true,
        usePolaris: true,
        listenForChanges: true,
        text: 'Reviews',
        lang: window.reviewsIoLang || 'en',
        mode: 'default',
        showEmptyStars: false
      });
    }

    renderChips(counts) {
      var info = this.colourInfo;
      var handles = Object.keys(counts).filter(function (h) { return info[h]; });
      if (this.colour && handles.indexOf(this.colour) === -1 && info[this.colour]) handles.push(this.colour);
      handles.sort(function (a, b) { return (counts[b] || 0) - (counts[a] || 0) || info[a].name.localeCompare(info[b].name); });
      var colour = this.colour;
      var html = '<button type="button" class="lux-hf__chip lux-hf__chip--any" data-colour="" aria-pressed="' + (!colour) + '">Any colour</button>';
      handles.forEach(function (h) {
        var dot = dotStyle(info[h], h);
        html += '<button type="button" class="lux-hf__chip" data-colour="' + esc(h) + '" aria-pressed="' + (h === colour) + '">' +
          '<span class="lux-hf__dot"' + dot + '></span>' + esc(info[h].name) + ' <span class="text--subdued">' + (counts[h] || 0) + '</span></button>';
      });
      this.chips.innerHTML = html;
      this.chipsWrap.hidden = handles.length < 2;
    }

    renderStyleChips(counts) {
      var style = this.helmetStyle;
      var shown = STYLES.filter(function (s) { return counts[s[0]] || s[0] === style; });
      var html = '<button type="button" class="lux-hf__chip lux-hf__chip--any" data-style="" aria-pressed="' + (!style) + '">Any style</button>';
      shown.forEach(function (s) {
        html += '<button type="button" class="lux-hf__chip lux-hf__chip--any" data-style="' + esc(s[0]) + '" aria-pressed="' + (s[0] === style) + '">' +
          esc(s[1]) + ' <span class="text--subdued">' + (counts[s[0]] || 0) + '</span></button>';
      });
      this.styleChips.innerHTML = html;
      // A collection of one style (race full face, say) has nothing to choose.
      this.styleWrap.hidden = shown.length < 2 && !style;
    }

    group(title, cards) {
      var self = this;
      return '<div class="lux-hf__group">' +
        (title ? '<h3 class="lux-hf__group-title heading h5">' + esc(title) + ' (' + cards.length + ')</h3>' : '') +
        '<div class="product-list"><div class="product-list__inner">' + cards.map(function (c) { return self.card(c); }).join('') + '</div></div></div>';
    }

    /* The variant a card is showing: its chosen colour and size, falling back
       to the best fitting size in that colour when the size does not come in it. */
    pick(c) {
      var inColour = c.fitting.filter(function (x) { return !c.colour || x.colour === c.colour; });
      return inColour.find(function (x) { return x.size === c.size; }) ||
        inColour.reduce(function (a, b) { return b.v.lux > a.v.lux ? b : a; }, inColour[0]);
    }

    /*
     * A result card is the collection tile (snippets/product-item.liquid,
     * layout A in assets/lux-tile-a.css) with the same classes, so it picks up
     * the same styles: photo with the brand logo and "+" quick view on it,
     * title, price and stars on one row, colour swatches left and size chips
     * right. Under that the finder adds the head size it fits, where the stock
     * is and Add to cart. Swatches and chips are buttons, not the tile's
     * links, so custom.js leaves them to the finder. Only the price and the
     * body are redrawn when an option is picked: Reviews.io binds the stars once.
     */
    card(c) {
      var p = c.item.p;
      if (c.colour === undefined) { c.colour = c.best.colour; c.size = c.best.size; }
      var cur = this.pick(c);
      var url = p.url + '?variant=' + cur.v.id;
      var image = cur.v.image || p.image;
      var qvId = 'lux-hf-qv-' + p.id;
      var plus = '<svg focusable="false" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 2.5v11M2.5 8h11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path></svg>';
      var join = p.url.indexOf('?') === -1 ? '?' : '&';

      return '<div class="product-item lux-hf__card" data-card="' + c.index + '">' +
        '<div class="product-item__image-wrapper">' +
          '<a class="product-item__aspect-ratio aspect-ratio aspect-ratio--square" style="padding-bottom: 100%; --aspect-ratio: 1" href="' + esc(url) + '" tabindex="-1" aria-hidden="true" data-card-link>' +
            (image ? '<img class="product-item__primary-image" src="' + esc(image) + '" alt="" loading="lazy" data-card-img>' : '') + '</a>' +
          (p.brand_logo
            ? '<div class="lux-tile-brand"><a href="' + esc(p.brand_url || p.url) + '" class="lux-tile-brand__link"><img src="' + esc(p.brand_logo) + '" alt="' + esc(p.vendor) + '" class="product-item__brand-logo" loading="lazy"></a></div>'
            : '') +
          '<button is="toggle-button" loader aria-controls="' + qvId + '-drawer" aria-expanded="false" class="lux-tile-qv hidden-phone" aria-label="Quick view">' + plus + '</button>' +
          '<button is="toggle-button" aria-controls="' + qvId + '-popover" aria-expanded="false" class="lux-tile-qv hidden-tablet-and-up" aria-label="Quick view">' + plus + '</button>' +
          '<quick-buy-popover id="' + qvId + '-popover" href="' + esc(p.url + join + 'view=quick-buy-popover') + '" class="popover popover--quick-buy hidden-tablet-and-up"></quick-buy-popover>' +
          '<quick-buy-drawer id="' + qvId + '-drawer" href="' + esc(p.url + join + 'view=quick-buy-drawer') + '" class="drawer drawer--large drawer--quick-buy hidden-phone"></quick-buy-drawer>' +
        '</div>' +
        '<div class="product-item__info"><div class="product-item-meta">' +
          '<a href="' + esc(url) + '" class="product-item-meta__title tw-font-extrabold" data-card-link><span class="product-item-meta__title-text">' + esc(p.title) + '</span></a>' +
          '<div class="lux-tile-buyrow">' +
            '<div class="product-item-meta__price-list-container" data-card-price>' + this.cardPrice(cur) + '</div>' +
            '<div class="product-item-meta__rating"><div class="ruk_rating_snippet" data-sku="' + esc(p.review_sku) + '" style="padding: 5px;"></div></div>' +
          '</div>' +
          '<div class="lux-hf__card-body" data-card-body>' + this.cardBody(c, cur) + '</div>' +
        '</div></div>' +
        '</div>';
    }

    cardPrice(cur) {
      return '<div class="price-list">' +
        '<span class="price' + (cur.v.compare ? ' price--highlight' : '') + '">' + esc(cur.v.price) + '</span>' +
        (cur.v.compare ? '<span class="price price--compare">' + esc(cur.v.compare) + '</span>' : '') +
        '</div>';
    }

    cardBody(c, cur) {
      var p = c.item.p;
      c.size = cur.size;

      // Colour swatches: every colour that comes in a fitting size.
      var colourOrder = [];
      var byColour = {};
      c.fitting.forEach(function (x) {
        if (!x.colour) return;
        var have = byColour[x.colour];
        if (!have) { colourOrder.push(x.colour); byColour[x.colour] = x; }
        else if (x.v.lux > have.v.lux) byColour[x.colour] = x;
      });
      var colours = '';
      if (colourOrder.length > 1) {
        colours = '<div class="product-item-meta__swatch-list lux-scroller color-swatch-list color-swatch-list--mini">' +
          colourOrder.map(function (name) {
            var img = byColour[name].v.image || p.image;
            return '<div class="color-swatch" data-tooltip="' + esc(name) + '"><button type="button" class="color-swatch__item color-swatch__item--image' + (name === cur.colour ? ' is-active' : '') + '" data-pick-colour="' + esc(name) + '" aria-pressed="' + (name === cur.colour) + '" aria-label="' + esc(name) + '">' +
              (img ? '<img src="' + esc(img.replace('width=400', 'width=120')) + '" alt="" width="120" height="120" loading="lazy">' : '') + '</button></div>';
          }).join('') + '</div>';
      }

      // Size chips: the fitting sizes that come in the chosen colour.
      var sizeSeen = {};
      var sizeBtns = [];
      c.fitting.forEach(function (x) {
        if (c.colour && x.colour !== cur.colour) return;
        var key = x.size || 'One size';
        if (sizeSeen[key]) return;
        sizeSeen[key] = true;
        sizeBtns.push('<button type="button" class="card-option-pill' + (x.size === cur.size ? ' is-active' : '') + '" data-pick-size="' + esc(x.size) + '" aria-pressed="' + (x.size === cur.size) + '" aria-label="Size ' + esc(key) + ', ' + fmt(x.range.min) + '-' + fmt(x.range.max) + 'cm">' + esc(key) + '</button>');
      });
      var sizes = sizeBtns.length > 1
        ? '<div class="product-item-meta__swatch-list lux-scroller product-item-meta__options-list color-swatch-list--mini">' + sizeBtns.join('') + '</div>'
        : '';

      var variants = (colours || sizes) ? '<div class="lux-tile-variants">' + colours + sizes + '</div>' : '';
      var fit = '<p class="lux-hf__fit">' + esc(cur.size || 'One size') + ' fits heads ' + fmt(cur.range.min) + '-' + fmt(cur.range.max) + 'cm</p>';

      var stock = cur.v.lux > 0
        ? '<p class="lux-hf__stock lux-hf__stock--lux"><span><strong>' + cur.v.lux + '</strong> in stock at LUXBMX</span></p>'
        : '<p class="lux-hf__stock lux-hf__stock--supplier">In stock with supplier</p>';

      return variants + fit + stock +
        '<button type="button" class="button button--primary button--small lux-hf__add" data-add="' + cur.v.id + '">Add to cart</button>' +
        '<span class="lux-hf__add-msg" role="status" data-add-msg></span>';
    }

    onResultsClick(e) {
      var el = e.target.closest('[data-pick-colour], [data-pick-size], [data-add]');
      if (!el) return;
      var cardEl = el.closest('[data-card]');
      var c = this.cards[Number(cardEl.dataset.card)];
      if (el.dataset.add) return this.addToCart(el, cardEl);
      var isColour = el.hasAttribute('data-pick-colour');
      if (isColour) c.colour = el.dataset.pickColour; else c.size = el.dataset.pickSize;
      var cur = this.pick(c);
      var p = c.item.p;
      cardEl.querySelector('[data-card-body]').innerHTML = this.cardBody(c, cur);
      cardEl.querySelector('[data-card-price]').innerHTML = this.cardPrice(cur);
      var img = cardEl.querySelector('[data-card-img]');
      if (img && (cur.v.image || p.image)) img.src = cur.v.image || p.image;
      cardEl.querySelectorAll('[data-card-link]').forEach(function (a) { a.href = p.url + '?variant=' + cur.v.id; });
      var again = cardEl.querySelector(isColour
        ? '[data-pick-colour="' + CSS.escape(c.colour) + '"]'
        : '[data-pick-size="' + CSS.escape(c.size) + '"]');
      if (again) again.focus();
    }

    /* Same request and events as the theme's own product form, so the cart
       count updates and the cart drawer opens with the new line in it. */
    addToCart(btn, cardEl) {
      var msg = cardEl.querySelector('[data-add-msg]');
      var routes = (window.themeVariables && window.themeVariables.routes) || {};
      var cartAdd = (routes.cartAddUrl || '/cart/add') + '.js';
      var cartUrl = (routes.cartUrl || '/cart') + '.js';
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      msg.textContent = '';
      fetch(cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.add), quantity: 1 }], sections: 'mini-cart' })
      }).then(function (r) {
        return r.json().then(function (json) { return { ok: r.ok, json: json }; });
      }).then(function (res) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        if (!res.ok) {
          msg.textContent = res.json.description || 'This one could not be added. Try another size or colour.';
          return;
        }
        msg.textContent = 'Added to your cart.';
        return fetch(cartUrl).then(function (r) { return r.json(); }).then(function (cart) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
          cart.sections = res.json.sections;
          var drawer = window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.cartType === 'drawer';
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart, openMiniCart: drawer } }));
        });
      }).catch(function () {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        msg.textContent = 'This one could not be added. Check your connection and try again.';
      });
    }

    showGrid(show) {
      if (this.grid) this.grid.style.display = show ? '' : 'none';
      document.body.classList.toggle('lux-hf-active', !show);
    }

    clear() {
      this.pinnedCm = null;
      this.input.value = '';
      this.colour = '';
      this.helmetStyle = '';
      this.update();
      this.input.focus();
    }

    syncUrl() {
      var url = new URL(location.href);
      var value = parseFloat(this.input.value);
      if (value > 0) url.searchParams.set('head', fmt(value)); else url.searchParams.delete('head');
      if (value > 0 && this.unit === 'in') url.searchParams.set('unit', 'in'); else url.searchParams.delete('unit');
      if (value > 0 && this.colour) url.searchParams.set('colour', this.colour); else url.searchParams.delete('colour');
      if (value > 0 && this.helmetStyle) url.searchParams.set('style', this.helmetStyle); else url.searchParams.delete('style');
      history.replaceState(history.state, '', url.toString());
    }
  }

  customElements.define('lux-helmet-finder', LuxHelmetFinder);

  /*
   * <lux-helmet-fit>, rendered by snippets/lux-helmet-fit.liquid under the
   * Size buttons of a helmet's buy grid. Uses the measurement saved by the
   * finder (or typed here) to say which size fits, marks that button, and
   * selects it on request by clicking the buy grid's own button, so the buy
   * grid stays in charge of the selection.
   */
  class LuxHelmetFit extends HTMLElement {
    connectedCallback() {
      var data = JSON.parse(this.querySelector('[data-guide]').textContent);
      this.rows = guideRows(data);
      this.option = this.closest('[data-option-index]');
      this.body = this.querySelector('[data-fit-body]');
      var self = this;
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-fit-go]')) self.submit();
        var sel = e.target.closest('[data-fit-select]');
        if (sel) {
          var pill = self.pills().find(function (b) { return b.dataset.optionValue === sel.dataset.fitSelect; });
          if (pill) pill.click();
          self.render(false);
        }
        if (e.target.closest('[data-fit-change]')) self.render(true);
      });
      this.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && e.target.closest('[data-fit-input]')) { e.preventDefault(); self.submit(); }
      });
      // Re-mark after the buy grid changes selection, so "Select M" goes away once M is chosen.
      if (this.option) {
        this.option.addEventListener('click', function (e) {
          if (e.target.closest('[data-option-value]')) setTimeout(function () { if (!self.querySelector('[data-fit-input]')) self.render(false); }, 0);
        });
      }
      this.render(false);
    }

    submit() {
      var input = this.querySelector('[data-fit-input]');
      var unit = this.querySelector('[data-fit-unit]').value;
      var v = parseFloat(input.value);
      var cm = unit === 'in' ? v * CM_PER_IN : v;
      if (!(cm >= MIN_CM && cm <= MAX_CM)) {
        this.querySelector('[data-fit-error]').textContent = 'Head measurements are usually between 45 and 65cm (18 to 26in).';
        return;
      }
      saveHead(cm);
      this.render(false);
    }

    pills() {
      return this.option ? Array.from(this.option.querySelectorAll('[data-option-value]')) : [];
    }

    render(editing) {
      var cm = savedHead();
      var rows = this.rows;
      var pills = this.pills();
      pills.forEach(function (b) { b.classList.remove('is-your-size'); });

      if (editing || cm == null) {
        this.body.innerHTML =
          '<div class="lux-fit__form">' +
            '<label class="lux-fit__prompt" for="' + this.id + '-in">' + (cm == null ? 'Not sure of your size? Enter your head measurement:' : 'Your head measurement:') + '</label>' +
            '<span class="lux-fit__row">' +
              '<input id="' + this.id + '-in" class="lux-fit__input" type="number" inputmode="decimal" step="0.5" min="1" placeholder="e.g. 56" data-fit-input' + (cm ? ' value="' + fmt(cm) + '"' : '') + '>' +
              '<select class="lux-fit__unit" data-fit-unit aria-label="Unit"><option value="cm">cm</option><option value="in">in</option></select>' +
              '<button type="button" class="lux-fit__go link" data-fit-go>Show my size</button>' +
            '</span>' +
            '<span class="lux-fit__error" data-fit-error></span>' +
          '</div>';
        return;
      }

      var fits = [];
      pills.forEach(function (b) {
        var r = rangeFor(b.dataset.optionValue, rows);
        if (r && cm >= r.min && cm <= r.max) {
          b.classList.add('is-your-size');
          fits.push({ value: b.dataset.optionValue, range: r, selected: b.classList.contains('is-selected') });
        }
      });

      var change = ' <button type="button" class="lux-fit__change link" data-fit-change>Change</button>';
      if (!fits.length) {
        this.body.innerHTML = '<p class="lux-fit__line">None of these sizes fit a ' + fmt(cm) + 'cm head.' + change + '</p>';
        return;
      }
      var names = fits.map(function (f) {
        return '<strong>' + esc(f.value) + '</strong> (' + fmt(f.range.min) + '-' + fmt(f.range.max) + 'cm)';
      }).join(' or ');
      var select = fits.length === 1 && !fits[0].selected
        ? ' <button type="button" class="lux-fit__select link" data-fit-select="' + esc(fits[0].value) + '">Select ' + esc(fits[0].value) + '</button>'
        : '';
      this.body.innerHTML = '<p class="lux-fit__line">Your size: ' + names + ' for a ' + fmt(cm) + 'cm head.' + select + change + '</p>';
    }
  }

  customElements.define('lux-helmet-fit', LuxHelmetFit);
})();
